import Papa from 'papaparse';
import { parseFreshaPhone } from './freshaCustomerCsv';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

const HEADER_ALIASES = {
  firstName: ['first name'],
  lastName: ['last name'],
  phone: ['phone number', 'phone'],
  email: ['email'],
  status: ['status'],
  birthday: ['birthday', 'birth date'],
  jobTitle: ['job title'],
  freshaTeamMemberId: ['team member id'],
  userPermission: ['user permission'],
};

export const FRESHA_PERMISSION_OPTIONS = [
  { value: 'Basic', roleLabelKey: 'dataImport.teamMembers.roles.staff' },
  { value: 'High', roleLabelKey: 'dataImport.teamMembers.roles.manager' },
  { value: 'Owner', roleLabelKey: 'dataImport.teamMembers.roles.full' },
];

const detectDelimiter = (text) => {
  const firstLine = (text.split(/\r?\n/).find((line) => {
    const trimmed = line.trim();
    return trimmed && !trimmed.toUpperCase().startsWith('SEP=');
  }) || '');
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return semicolons >= commas ? ';' : ',';
};

const normalizeWhitespace = (value) => (value == null ? '' : String(value).trim());

const normalizeHeader = (value) => normalizeWhitespace(value).toLowerCase();

const stripSepDirective = (text) => text.replace(/^\uFEFF?SEP=.+\r?\n/i, '');

const findHeaderIndex = (headers, aliases) => {
  const normalizedHeaders = headers.map(normalizeHeader);
  for (const alias of aliases) {
    const index = normalizedHeaders.indexOf(alias);
    if (index >= 0) return index;
  }
  return -1;
};

const buildHeaderMap = (headers) => {
  const map = {};
  Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
    map[key] = findHeaderIndex(headers, aliases);
  });
  return map;
};

export const buildTeamMemberName = (firstName, lastName) => (
  [normalizeWhitespace(firstName), normalizeWhitespace(lastName)].filter(Boolean).join(' ')
);

export const parseFreshaBirthDate = (rawValue) => {
  const raw = normalizeWhitespace(rawValue);
  if (!raw) {
    return { birthDate: null, warningKey: null, original: '' };
  }

  const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) {
    return { birthDate: null, warningKey: 'dataImport.teamMembers.warnings.invalidBirthDate', original: raw };
  }

  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return { birthDate: null, warningKey: 'dataImport.teamMembers.warnings.invalidBirthDate', original: raw };
  }

  const monthStr = String(month).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  return {
    birthDate: `${year}-${monthStr}-${dayStr}`,
    warningKey: null,
    original: raw,
  };
};

export const parseFreshaStatus = (rawValue) => {
  const normalized = normalizeWhitespace(rawValue).toLowerCase();
  if (normalized === 'archived') {
    return { disabled: true, warningKey: null };
  }
  if (normalized === 'active' || !normalized) {
    return { disabled: false, warningKey: null };
  }
  return { disabled: false, warningKey: 'dataImport.teamMembers.warnings.unknownStatus' };
};

export const parseFreshaUserPermission = (rawValue) => {
  const raw = normalizeWhitespace(rawValue);
  if (!raw) {
    return {
      userPermission: 'Basic',
      warningKey: 'dataImport.teamMembers.warnings.unknownPermission',
      original: '',
    };
  }

  const known = FRESHA_PERMISSION_OPTIONS.some(
    (option) => option.value.toLowerCase() === raw.toLowerCase(),
  );
  if (!known) {
    return {
      userPermission: 'Basic',
      warningKey: 'dataImport.teamMembers.warnings.unknownPermission',
      original: raw,
    };
  }

  const matched = FRESHA_PERMISSION_OPTIONS.find(
    (option) => option.value.toLowerCase() === raw.toLowerCase(),
  );
  return {
    userPermission: matched.value,
    warningKey: null,
    original: raw,
  };
};

const getValue = (values, index) => (index >= 0 ? normalizeWhitespace(values[index]) : '');

const buildRowFromValues = (values, rowNumber, headerMap) => {
  const errors = [];
  const warnings = [];

  const firstName = getValue(values, headerMap.firstName);
  const lastName = getValue(values, headerMap.lastName);
  const name = buildTeamMemberName(firstName, lastName);
  const email = getValue(values, headerMap.email);
  const freshaTeamMemberId = getValue(values, headerMap.freshaTeamMemberId);
  const jobTitle = getValue(values, headerMap.jobTitle);

  if (!name) {
    errors.push('dataImport.teamMembers.errors.nameRequired');
  }
  if (!email) {
    errors.push('dataImport.teamMembers.errors.emailRequired');
  } else if (!EMAIL_REGEX.test(email)) {
    errors.push('dataImport.teamMembers.errors.emailInvalid');
  }

  const phoneResult = parseFreshaPhone(getValue(values, headerMap.phone));
  if (phoneResult.warningKey) {
    warnings.push(phoneResult.warningKey);
  }

  const birthDateResult = parseFreshaBirthDate(getValue(values, headerMap.birthday));
  if (birthDateResult.warningKey) {
    warnings.push(birthDateResult.warningKey);
  }

  const statusResult = parseFreshaStatus(getValue(values, headerMap.status));
  if (statusResult.warningKey) {
    warnings.push(statusResult.warningKey);
  }

  const permissionResult = parseFreshaUserPermission(getValue(values, headerMap.userPermission));
  if (permissionResult.warningKey) {
    warnings.push(permissionResult.warningKey);
  }

  return {
    rowNumber,
    freshaTeamMemberId,
    name,
    firstName,
    lastName,
    email,
    phone: phoneResult.phone,
    phoneOriginal: phoneResult.original,
    birthDate: birthDateResult.birthDate,
    birthDateOriginal: birthDateResult.original,
    jobTitle,
    userPermission: permissionResult.userPermission,
    disabled: statusResult.disabled,
    errors,
    warnings,
    rawValues: values,
  };
};

export const revalidateTeamMemberRow = (row) => {
  const draft = {
    freshaTeamMemberId: normalizeWhitespace(row.freshaTeamMemberId),
    name: normalizeWhitespace(row.name),
    email: normalizeWhitespace(row.email),
    phone: row.phone || null,
    birthDate: row.birthDate || null,
    jobTitle: normalizeWhitespace(row.jobTitle),
    userPermission: normalizeWhitespace(row.userPermission) || 'Basic',
    disabled: Boolean(row.disabled),
  };

  const errors = [];
  const warnings = [];

  if (!draft.name) {
    errors.push('dataImport.teamMembers.errors.nameRequired');
  }
  if (!draft.email) {
    errors.push('dataImport.teamMembers.errors.emailRequired');
  } else if (!EMAIL_REGEX.test(draft.email)) {
    errors.push('dataImport.teamMembers.errors.emailInvalid');
  }

  const permissionResult = parseFreshaUserPermission(draft.userPermission);
  if (permissionResult.warningKey) {
    warnings.push(permissionResult.warningKey);
  }

  return {
    ...row,
    ...draft,
    userPermission: permissionResult.userPermission,
    errors,
    warnings,
  };
};

export const parseFreshaEmployeeCsv = (text) => {
  const cleaned = stripSepDirective(text || '');
  if (!normalizeWhitespace(cleaned)) {
    return { errorKey: 'dataImport.teamMembers.errors.emptyFile', rows: [], headers: [], delimiter: ',' };
  }

  const delimiter = detectDelimiter(cleaned);
  const parsed = Papa.parse(cleaned, {
    delimiter,
    skipEmptyLines: 'greedy',
  });

  if (!parsed.data?.length) {
    return { errorKey: 'dataImport.teamMembers.errors.emptyFile', rows: [], headers: [], delimiter };
  }

  const headers = (parsed.data[0] || []).map((value) => normalizeWhitespace(value));
  const headerMap = buildHeaderMap(headers);

  if (headerMap.email < 0 || headerMap.firstName < 0) {
    return {
      errorKey: 'dataImport.teamMembers.errors.unsupportedHeaders',
      rows: [],
      headers,
      delimiter,
    };
  }

  const rows = parsed.data
    .slice(1)
    .map((values, index) => buildRowFromValues(values, index + 2, headerMap))
    .filter((row) => row.rawValues.some((value) => normalizeWhitespace(value)));

  if (!rows.length) {
    return { errorKey: 'dataImport.teamMembers.errors.emptyFile', rows: [], headers, delimiter };
  }

  return { errorKey: null, rows, headers, delimiter };
};

export const buildImportTeamMembersPayload = (rows) => ({
  teamMembers: rows.map((row) => ({
    freshaTeamMemberId: row.freshaTeamMemberId || null,
    name: row.name,
    email: row.email,
    phone: row.phone || null,
    birthDate: row.birthDate || null,
    jobTitle: row.jobTitle || null,
    userPermission: row.userPermission || 'Basic',
    disabled: Boolean(row.disabled),
  })),
});

export const buildFailingTeamMembersCsv = (failedRows, headers, delimiter = ',') => {
  const lines = [Papa.unparse([headers], { delimiter })];
  failedRows.forEach((row) => {
    lines.push(Papa.unparse([row.rawValues || []], { delimiter }));
  });
  return lines.join('\n');
};
